/* src/pages/CustomDomains.jsx
 * Custom Domain Management Page
 * Manage all custom domains for your workspace
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useToast } from '../contexts/ToastContext';
import InlineConfirm from '../components/InlineConfirm';
import SenderPicker from '../components/SenderPicker';
import DnsRecordsPanel from '../components/DnsRecordsPanel';
import { useIsMobile } from '../hooks/useIsMobile';
import { domainApi } from '../utils/api';
import { domainSenderName } from '../utils/replyIdentity';

export default function CustomDomains() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { selectedWorkspaceId } = useWorkspace();
  const { showToast } = useToast();
  const isMobile = useIsMobile();
  
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingStatus, setCheckingStatus] = useState({});
  const [deletingDomain, setDeletingDomain] = useState({});
  const [enablingInbound, setEnablingInbound] = useState({});
  const [checkingMx, setCheckingMx] = useState({});
  const [mxResults, setMxResults] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editPrefix, setEditPrefix] = useState('');
  const [editName, setEditName] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [prefixChangeArmed, setPrefixChangeArmed] = useState(false);

  useEffect(() => {
    if (currentUser && selectedWorkspaceId) {
      loadDomains();
    }
  }, [currentUser, selectedWorkspaceId]);

  function sortDomains(domainsList) {
    return [...domainsList].sort((a, b) => {
      if (a.verificationStatus === 'verified' && b.verificationStatus !== 'verified') return -1;
      if (a.verificationStatus !== 'verified' && b.verificationStatus === 'verified') return 1;
      const aMs = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
      const bMs = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
      return bMs - aMs;
    });
  }

  async function loadDomains({ silent = false } = {}) {
    if (!selectedWorkspaceId) return;

    if (!silent) setLoading(true);
    try {
      const domainsRef = collection(db, 'email_domains');
      const domainQuery = query(
        domainsRef,
        where('workspaceId', '==', selectedWorkspaceId)
      );
      const domainSnapshot = await getDocs(domainQuery);

      const domainsList = [];
      domainSnapshot.forEach((docSnap) => {
        domainsList.push({ id: docSnap.id, ...docSnap.data() });
      });

      setDomains(sortDomains(domainsList));
    } catch (error) {
      console.error('Error loading domains:', error);
      showToast('Error loading domains: ' + (error.message || error), 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  }

  async function handleSenderAdded(result) {
    if (result.domain?.id) {
      setDomains((prev) => sortDomains([
        result.domain,
        ...prev.filter((d) => d.id !== result.domain.id && d.fromEmail !== result.domain.fromEmail),
      ]));
    }
    await loadDomains({ silent: true });
  }

  async function handleDeleteDomain(domainId) {
    setDeletingDomain({ ...deletingDomain, [domainId]: true });
    try {
      const result = await domainApi.delete({
        domainId,
        workspaceId: selectedWorkspaceId,
      });
      
      if (result.success) {
        await loadDomains();
        showToast('Domain deleted successfully');
      } else {
        showToast('Error: ' + (result.error || 'Failed to delete domain'), 'error');
      }
    } catch (error) {
      console.error('Error deleting domain:', error);
      showToast('Error deleting domain. Check console for details.', 'error');
    } finally {
      setDeletingDomain({ ...deletingDomain, [domainId]: false });
    }
  }

  async function handleCheckStatus(domainId, fromEmail) {
    setCheckingStatus({ ...checkingStatus, [domainId]: true });
    try {
      const result = await domainApi.checkStatus(fromEmail);
      
      // Debug: Log the result to console
      console.log('[CustomDomains] Check status result:', result);
      
      if (result.verified) {
        const domainRef = doc(db, 'email_domains', domainId);
        await updateDoc(domainRef, {
          verificationStatus: 'verified',
          waitingOn: 'none',
          postmarkConfirmed: true,
          postmarkDkimVerified: result.details?.dkimVerified === true,
          postmarkReturnPathVerified: result.details?.returnPathVerified === true,
          verifiedAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
        await loadDomains();
        showToast('Domain verified!');
      } else {
        const domainRef = doc(db, 'email_domains', domainId);
        await updateDoc(domainRef, {
          verificationStatus: 'pending',
          waitingOn: result.waitingOn || 'dns',
          postmarkConfirmed: result.confirmed === true,
          postmarkDkimVerified: result.details?.dkimVerified === true,
          postmarkReturnPathVerified: result.details?.returnPathVerified === true,
          updatedAt: Timestamp.now(),
        });
        let message = '';
        if (result.waitingOn === 'email_confirmation') {
          message = 'DNS is verified. Waiting on email confirmation (Confirmed=false).\n\nCheck the inbox for this sender and click the Postmark link. We resent the confirmation email.';
        } else {
          message = '⏳ Waiting on DNS.\n\n';
          message += `Confirmed: ${result.confirmed ? 'true' : 'false'}\n`;
          if (result.details) {
            message += `DKIMVerified: ${result.details.dkimVerified ? 'true' : 'false'} (${result.details.dkimStatus})\n`;
            message += `ReturnPathDomainVerified: ${result.details.returnPathVerified ? 'true' : 'false'} (${result.details.returnPathStatus})\n`;
            if (result.details.issues && result.details.issues.length > 0) {
              message += `\n${result.details.issues.join('\n')}`;
            }
          }
        }
        showToast(message, 'info');
        await loadDomains();
      }
    } catch (error) {
      console.error('Error checking status:', error);
      showToast('Error checking status: ' + error.message, 'error');
    } finally {
      setCheckingStatus({ ...checkingStatus, [domainId]: false });
    }
  }

  async function handleEnableInbound(domainId) {
    setEnablingInbound({ ...enablingInbound, [domainId]: true });
    try {
      const result = await domainApi.enableInbound({
        domainId,
        workspaceId: selectedWorkspaceId,
      });
      if (result.success) {
        await loadDomains();
        showToast('Inbound server created. Now add the MX record shown below.');
      } else {
        showToast('Error: ' + (result.error || 'Failed to enable inbound'), 'error');
      }
    } catch (error) {
      console.error('Error enabling inbound:', error);
      showToast('Error enabling inbound. Check console for details.', 'error');
    } finally {
      setEnablingInbound({ ...enablingInbound, [domainId]: false });
    }
  }

  async function handleCheckMx(domainId, domainName) {
    setCheckingMx({ ...checkingMx, [domainId]: true });
    try {
      const result = await domainApi.checkMx(domainName);
      setMxResults({ ...mxResults, [domainId]: result });
    } catch (error) {
      console.error('Error checking MX:', error);
      setMxResults({ ...mxResults, [domainId]: { success: false, message: 'Error checking MX records' } });
    } finally {
      setCheckingMx({ ...checkingMx, [domainId]: false });
    }
  }

  // Get display status for a domain card
  function getDomainStatus(domain) {
    if (domain.verificationStatus === 'verified' && domain.inboundEnabled) return 'ready';
    if (domain.verificationStatus === 'verified') return 'sending_ready';
    if (domain.waitingOn === 'email_confirmation') return 'awaiting_email';
    return 'setup_in_progress';
  }

  const statusConfig = {
    ready: { label: 'Ready', chipClass: 'accent-soft' },
    sending_ready: { label: 'Sending Ready', chipClass: 'accent' },
    awaiting_email: { label: 'Awaiting email confirmation', chipClass: 'warn' },
    setup_in_progress: { label: 'Pending DNS Verification', chipClass: 'warn' },
  };

  function startEdit(domain) {
    const prefix = String(domain.fromEmail || '').split('@')[0] || '';
    setEditingId(domain.id);
    setEditPrefix(prefix);
    setEditName(domainSenderName(domain) || '');
    setPrefixChangeArmed(false);
  }

  function currentPrefix(domain) {
    return String(domain.fromEmail || '').split('@')[0] || '';
  }

  function prefixIsChanging(domain) {
    return editPrefix.trim().toLowerCase() !== currentPrefix(domain).toLowerCase();
  }

  async function handleSaveEdit(domain) {
    if (prefixIsChanging(domain) && !prefixChangeArmed) {
      setPrefixChangeArmed(true);
      return;
    }
    setSavingEdit(true);
    try {
      const result = await domainApi.update({
        domainId: domain.id,
        workspaceId: selectedWorkspaceId,
        displayName: editName,
        emailPrefix: editPrefix,
      });
      if (result.success) {
        setEditingId(null);
        setPrefixChangeArmed(false);
        await loadDomains();
        if (result.waitingOn === 'email_confirmation') {
          showToast('Sender updated. Confirm the new address in the Postmark email if the prefix changed.');
        } else {
          showToast('Sender updated.');
        }
      } else {
        showToast('Error: ' + (result.error || 'Failed to update'), 'error');
      }
    } catch (error) {
      console.error('Error updating domain:', error);
      showToast('Error updating domain.', 'error');
    } finally {
      setSavingEdit(false);
    }
  }

  function formatDate(timestamp) {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  const pagePad = isMobile ? '1rem 1rem 2rem' : '24px 32px 48px';

  const deleteButtonStyle = (domainId) => ({
    padding: '0.5rem 1rem',
    background: deletingDomain[domainId] ? 'var(--dim)' : 'var(--danger)',
    color: '#fff',
    border: 'none',
    borderRadius: '999px',
    cursor: deletingDomain[domainId] ? 'not-allowed' : 'pointer',
    fontSize: '0.85rem',
    fontWeight: '600',
    fontFamily: 'inherit',
  });

  function renderDnsContent(records, { includeSpf = false, verifyDomainId, verifyFromEmail } = {}) {
    if (!records) return null;
    return (
      <DnsRecordsPanel
        records={records}
        includeSpf={includeSpf}
        onCopy={(msg, tone) => showToast(msg, tone)}
        verifyButton={verifyDomainId && verifyFromEmail ? (
          <button
            type="button"
            className="inbox-pill-btn-primary domains-add-btn"
            style={{ marginTop: '0.75rem' }}
            onClick={() => handleCheckStatus(verifyDomainId, verifyFromEmail)}
            disabled={checkingStatus[verifyDomainId]}
          >
            {checkingStatus[verifyDomainId] ? 'Checking...' : 'Check Verification Status'}
          </button>
        ) : null}
      />
    );
  }

  if (loading) {
    return (
      <div className="console-shell" style={{ minHeight: '100vh', background: 'var(--bg)', minWidth: 0 }}>
        <div className="today-page domains-page" style={{ padding: pagePad }}>
          <p style={{ color: 'var(--muted)', margin: 0, textAlign: 'center' }}>Loading domains...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="console-shell" style={{ minHeight: '100vh', background: 'var(--bg)', minWidth: 0 }}>
      <div className="today-page domains-page" style={{ padding: pagePad }}>
        <div className="today-header">
          <div className="today-header-left" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
            <button type="button" className="inbox-pill-btn" onClick={() => navigate('/dashboard')} style={{ marginBottom: '0.5rem' }}>
              Back to Dashboard
            </button>
            <h1 className="today-h1">Custom Domains</h1>
            <p style={{ margin: 0, color: 'var(--muted)', fontSize: '0.95rem' }}>
              Manage your custom email domains for white-labeling
            </p>
          </div>
        </div>

        <div className="today-panel domains-section-panel">
          <div className="today-panel-header">
            <span className="today-panel-title">Add New Domain</span>
          </div>
          <div className="today-panel-body">
            <SenderPicker
              mode="manage"
              workspaceId={selectedWorkspaceId}
              senders={domains}
              showGoctaOption={false}
              showToast={showToast}
              onAdded={handleSenderAdded}
              onCheckStatus={(domainId, fromEmail) => handleCheckStatus(domainId, fromEmail)}
            />
          </div>
        </div>

        <div className="domains-section-panel">
          <h2 className="domains-list-title">Your Domains ({domains.length})</h2>

          {domains.length === 0 ? (
            <div className="today-panel domains-empty-panel">
              <p className="domains-empty-text">No domains added yet. Add your first domain above!</p>
            </div>
          ) : (
            <div className="domains-domain-list">
              {domains.map((domain) => {
                const status = getDomainStatus(domain);
                const cfg = statusConfig[status];
                return (
                  <div
                    key={domain.id}
                    className={`today-panel domains-domain-card${domain.verificationStatus === 'verified' ? ' domains-domain-card--verified' : ' domains-domain-card--pending'}`}
                  >
                    <div className="today-panel-body">
                      <div className="domains-domain-header">
                        <div className="domains-domain-name-row">
                          <span>{domain.domain}</span>
                          <span className={`inbox-chip ${cfg.chipClass}`}>{cfg.label}</span>
                        </div>
                        <p className="domains-domain-meta">
                          <strong>Email:</strong> {domain.fromEmail}<br />
                          <strong>Display Name:</strong> {domainSenderName(domain) || domain.domain}<br />
                          <strong>Added:</strong> {formatDate(domain.createdAt)}<br />
                          <strong>Confirmed:</strong> {domain.postmarkConfirmed ? 'true' : 'false'} ·{' '}
                          <strong>DKIMVerified:</strong> {domain.postmarkDkimVerified ? 'true' : 'false'} ·{' '}
                          <strong>ReturnPathDomainVerified:</strong> {domain.postmarkReturnPathVerified ? 'true' : 'false'}
                        </p>
                      </div>

                      {editingId === domain.id && (
                        <div className="domains-add-form" style={{ marginBottom: '1rem' }}>
                          <div className="domains-field">
                            <label className="domains-field-label">Email prefix</label>
                            <input
                              type="text"
                              className="fb-input"
                              value={editPrefix}
                              onChange={(e) => {
                                setEditPrefix(e.target.value);
                                setPrefixChangeArmed(false);
                              }}
                            />
                            <p className="domains-field-hint">Will become: {editPrefix || 'replies'}@{domain.domain}</p>
                          </div>
                          <div className="domains-field">
                            <label className="domains-field-label">Display name</label>
                            <input
                              type="text"
                              className="fb-input"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                            />
                          </div>
                          {prefixChangeArmed && prefixIsChanging(domain) && (
                            <div className="fb-callout warn" style={{ margin: 0 }}>
                              <strong>Prefix change</strong>
                              <p style={{ fontSize: '0.85rem', margin: '0.5rem 0 0', lineHeight: 1.5 }}>
                                Sending from the old address stops immediately; the new address works once you confirm the email.
                              </p>
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              type="button"
                              className="inbox-pill-btn-primary"
                              onClick={() => handleSaveEdit(domain)}
                              disabled={savingEdit || !editPrefix}
                            >
                              {savingEdit
                                ? 'Saving...'
                                : prefixChangeArmed && prefixIsChanging(domain)
                                  ? 'Confirm prefix change'
                                  : 'Save'}
                            </button>
                            <button
                              type="button"
                              className="inbox-pill-btn"
                              onClick={() => {
                                setEditingId(null);
                                setPrefixChangeArmed(false);
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {domain.verificationStatus !== 'verified' && domain.waitingOn !== 'email_confirmation' && domain.dnsRecords && (
                        renderDnsContent(domain.dnsRecords, {
                          verifyDomainId: domain.id,
                          verifyFromEmail: domain.fromEmail,
                        })
                      )}

                      {domain.verificationStatus !== 'verified' && domain.waitingOn === 'email_confirmation' && (
                        <div className="fb-callout warn" style={{ marginBottom: '1rem' }}>
                          <strong>Waiting on email confirmation</strong>
                          <p style={{ fontSize: '0.85rem', margin: '0.5rem 0 0', lineHeight: 1.5 }}>
                            DKIM and Return-Path are already verified on this domain. Open the Postmark confirmation email sent to {domain.fromEmail} and click the link. Then check status.
                          </p>
                          <button
                            type="button"
                            className="inbox-pill-btn-primary domains-add-btn"
                            style={{ marginTop: '0.75rem' }}
                            onClick={() => handleCheckStatus(domain.id, domain.fromEmail)}
                            disabled={checkingStatus[domain.id]}
                          >
                            {checkingStatus[domain.id] ? 'Checking...' : 'Check Verification Status'}
                          </button>
                        </div>
                      )}

                      {domain.verificationStatus === 'verified' && (
                        <div className="today-panel domains-inbound-panel">
                          <div className="today-panel-header">
                            <span className="today-panel-title">Step 2: AI Replies (Inbound Email)</span>
                          </div>
                          <div className="today-panel-body">
                            {domain.inboundEnabled ? (
                              <>
                                <div className="fb-callout success" style={{ marginBottom: '1rem' }}>
                                  <strong>Inbound server created</strong>
                                  <p style={{ fontSize: '0.85rem', margin: '0.25rem 0 0', lineHeight: 1.5 }}>
                                    Replies to emails from {domain.domain} will be processed by AI.
                                  </p>
                                </div>
                                <div style={{ marginBottom: '0.75rem' }}>
                                  <p className="domains-dns-intro" style={{ marginBottom: '0.5rem' }}>
                                    <strong>MX Record:</strong> Add this record so replies reach the inbound server.
                                  </p>
                                  <table className="domains-mx-table">
                                    <thead>
                                      <tr>
                                        <th>Type</th>
                                        <th>Name</th>
                                        <th>Value</th>
                                        <th>Priority</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr>
                                        <td>MX</td>
                                        <td>@</td>
                                        <td>inbound.postmarkapp.com</td>
                                        <td>10</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                  <p className="domains-step-hint">
                                    If you already use Google Workspace or Microsoft 365 for email, use <code className="fb-code">replies</code> as the Name instead of <code className="fb-code">@</code> to avoid conflicts.
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  className="inbox-pill-btn-primary domains-add-btn"
                                  onClick={() => handleCheckMx(domain.id, domain.domain)}
                                  disabled={checkingMx[domain.id]}
                                >
                                  {checkingMx[domain.id] ? 'Checking MX...' : 'Check MX Record'}
                                </button>
                                {mxResults[domain.id] && (
                                  <div className={`fb-callout ${mxResults[domain.id].pointsToPostmark ? 'success' : 'warn'}`} style={{ marginTop: '0.75rem', marginBottom: 0 }}>
                                    {mxResults[domain.id].pointsToPostmark
                                      ? 'MX record correctly points to Postmark.'
                                      : mxResults[domain.id].message || 'MX record not pointing to Postmark yet. Add the MX record and wait for DNS propagation (15 min - 48 hours).'}
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                <p className="domains-dns-intro">
                                  Enable inbound to let AI automatically reply to emails sent to your custom domain.
                                  This creates a dedicated mail server for {domain.domain} (free, no extra cost).
                                </p>
                                <button
                                  type="button"
                                  className="inbox-pill-btn-primary domains-add-btn"
                                  onClick={() => handleEnableInbound(domain.id)}
                                  disabled={enablingInbound[domain.id]}
                                >
                                  {enablingInbound[domain.id] ? 'Creating Inbound Server...' : 'Enable Inbound (AI Replies)'}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {domain.verificationStatus !== 'verified' && domain.waitingOn !== 'email_confirmation' && domain.dnsRecords && (
                        <>
                          <h4 className="domains-step-label">Step 1: Verify Sending</h4>
                          <p className="domains-step-hint">Usually takes 15 min - 1 hour, up to 48 hours for DNS propagation.</p>
                        </>
                      )}

                      <div className="domains-domain-footer" style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className="inbox-pill-btn"
                          onClick={() => startEdit(domain)}
                          disabled={editingId === domain.id}
                        >
                          Edit
                        </button>
                        <InlineConfirm
                          message="Delete this domain? This cannot be undone."
                          onConfirm={() => handleDeleteDomain(domain.id)}
                          disabled={deletingDomain[domain.id]}
                          loading={deletingDomain[domain.id]}
                          buttonLabel={deletingDomain[domain.id] ? 'Deleting...' : 'Delete'}
                          loadingLabel="Deleting..."
                          buttonStyle={deleteButtonStyle(domain.id)}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
